<template>
  <el-collapse-item name="slack">
    <template #title>
      <div>
        <div class="el-collapse-item__image">
          <img src="./img/slack.svg" >
        </div>
        <span class="el-collapse-item__title">Slack</span>
        <span class="el-collapse-item__sub-title">A messaging app for teams</span>
      </div>
    </template>
    <div class="slack-integration">
      <h4>Send notifications to a channel</h4>
      <p>When you create items, add comments, or update items in any way, DoneDone will post a new message to a designated Slack channel. This is a great way to collaborate on any issues with your team in real-time.</p>
      <el-form
        v-if="slackAuthorized"
        ref="form"
        :disabled="isSubmitting || isRemoving"
        :rules="rules"
        :model="formData"
        label-position="top"
      >
        <el-form-item prop="room" label="Room">
          <single-select v-model="formData.room" placeholder="Choose a Room...">
            <el-option
              v-for="item in slackIntegration.listChannels"
              :key="item.id"
              :value="item.id"
              :label="item.name"
            />
          </single-select>
        </el-form-item>
      </el-form>
      <el-button
        v-if="!slackAuthorized"
        class="slack-integration__add-button"
        type="default"
        size="default"
        @click="goToSlackOAuthUrl"
      >
        <img src="./img/slack.svg" >Add Slack
      </el-button>
      <dd-button
        v-if="slackAuthorized"
        :is-disabled="isSubmitting"
        :is-loading="isRemoving"
        class="float-left"
        type="flat"
        size="medium"
        @click="removeHandler"
      >Remove Slack</dd-button>

      <dd-button
        v-if="slackAuthorized"
        :is-disabled="isRemoving"
        :is-loading="isSubmitting"
        :color="buttonColor"
        class="float-right"
        size="medium"
        @click="submitHandler"
      >Save Changes</dd-button>

      <div class="clearfix" />
    </div>
  </el-collapse-item>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { EEntities } from '@/types'
import {
  IGenericJSONMessage,
  IProjectsOutputIntegrationsSlackIntegrationInfo,
} from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import InternalProjectsAPI from '@/api/modules/internal-projects.api'

export default defineComponent({
  name: 'SlackIntegrationAccordion',

  props: {
    type: {
      type: String as () => EEntities,
      required: true,
    },
    entityId: {
      type: Number,
      required: true,
    },
    slackIntegration: {
      type: Object as () => IProjectsOutputIntegrationsSlackIntegrationInfo,
      required: true,
    },
  },

  data() {
    return {
      isSubmitting: false,
      isRemoving: false,
      formData: {
        room: '',
      },
    }
  },

  computed: {
    buttonColor(): string {
      return ColorAssistant.getEntityColor(this.type)
    },

    slackAuthorized(): boolean {
      return (
        this.slackIntegration.listChannels &&
        this.slackIntegration.listChannels.length > 0
      )
    },

    rules() {
      return {
        room: [
          {
            required: true,
            trigger: 'submit',
            message: 'You must select a room',
          },
        ],
      }
    },
  },

  watch: {
    'slackIntegration.channelID': {
      immediate: true,
      handler(newVal) {
        this.formData.room = newVal
      },
    },
  },

  methods: {
    async save(room: string): Promise<IGenericJSONMessage> {
      if (this.type === EEntities.MAILBOXES) {
        return await MailboxesAPI.post_SetSlackIntegration(this.entityId, room)
      } else {
        return await InternalProjectsAPI.post_SetSlackIntegration(
          this.entityId,
          room,
        )
      }
    },

    async remove(): Promise<IGenericJSONMessage> {
      if (this.type === EEntities.MAILBOXES) {
        return await MailboxesAPI.post_RemoveSlackIntegration(this.entityId)
      } else {
        return await InternalProjectsAPI.post_RemoveSlackIntegration(
          this.entityId,
        )
      }
    },

    goToSlackOAuthUrl() {
      // @ts-ignore
      window.location.href = this.slackIntegration.oAuthUrl
    },

    async submitHandler() {
      // @ts-ignore
      await this.$refs.form.validate(async valid => {
        if (valid) {
          this.isSubmitting = true
          try {
            const response = await this.save(this.formData.room)
            this.$messaging.showSuccess(response.message)
          } catch (err) {
            this.$error.emit(err, true)
          } finally {
            this.isSubmitting = false
          }
        }
      })
    },

    async removeHandler() {
      this.isRemoving = true
      try {
        const response = await this.remove()
        this.slackIntegration.listChannels = []
        this.$messaging.showSuccess(response.message)
      } catch (err) {
        this.$error.emit(err, true)
      } finally {
        this.isRemoving = false
      }
    },
  },
})
</script>

<style lang="scss" >
.slack-integration {
  p {
    margin-bottom: 24px;
  }

  /* Element UI's medium button, which Element Plus doesn't have */
  &__add-button.el-button {
    padding: 10px 12px;
    font-size: 13px;
  }

  &__add-button {
    img {
      width: 16px;
      margin-right: 8px;
    }
  }
}
</style>
